/**
 * 旧 URL（https://dokoiku-fukuoka.vercel.app）で使っていた人のお気に入り・履歴・保存したプランを
 * 新 URL（https://dokoiku-fukuoka.web.app）へ引き継ぐ、受け取り側の中身。2026-09-25 の移設で作った。
 *
 * localStorage はドメインごとなので、そのままでは新 URL で空になる。そこで
 *   1. 旧 URL の引き継ぎページ（vercel-redirect/index.html）が、旧ドメインの localStorage から
 *      下の 3 つのキーを読み、`#dokoiku-carry=<base64url の JSON>` を付けて新 URL へ location.replace する
 *   2. 新 URL の起動時（carryOver.web.js）に、ここの applyCarryOver が受け取って localStorage へ合わせ、
 *      history.replaceState でハッシュを消す
 * という 2 段で運ぶ。サーバーには何も送らない（ハッシュはリクエストに乗らない）。
 *
 * 行き先がトップ以外（/spots/xxx.html など、アプリ本体を読み込まない静的ページ）でも荷物を落とさないよう、
 * 旧 URL は荷物があるときはいつも新 URL の /carry.html（public/carry.html）を経由する。carry.html は荷物を
 * localStorage の CARRY_PENDING_KEY へ置いて本来の行き先へ移るだけで、ほどくのはアプリ本体（/）を開いたとき
 * （applyPendingCarryOver）。ハッシュで直接届いた場合（applyCarryOver）も同じ手順でほどく。
 *
 * ここはブラウザにも Node にも依存しない純粋な関数だけにしてある（scripts/verify-carry-over.mjs が Node で検査する）。
 *
 * 外してよい時期: 2026-12-25 以降（旧 URL の引き継ぎページを 301 に戻すのと同時）。CLAUDE.md の公開先の節を参照。
 */

export const CARRY_PARAM = "dokoiku-carry";
export const CARRY_VERSION = 1;
// 受け取る base64url 文字列の上限。実データは お気に入り 84 件＋履歴 30 件＋プラン 1 件でも 5KB 程度。
export const CARRY_MAX_CHARS = 50 * 1024;

// App.js の STORAGE_FAVORITES_KEY / STORAGE_HISTORY_KEY / STORAGE_PLAN_KEY と同じ値。変えたら両方を変えること。
export const CARRY_KEYS = {
  favorites: "fukuoka-go:favorites",
  history: "fukuoka-go:history",
  plan: "fukuoka-go:plan",
};
// carry.html が置いていく、まだほどいていない荷物（base64url の文字列そのまま）。
export const CARRY_PENDING_KEY = "dokoiku-carry:pending";
// 同じ荷物を二度ほどかないための控え（新 URL 側だけに置く）。
export const CARRY_SEEN_KEY = "dokoiku-carry:seen";
const SEEN_LIMIT = 10;
// App.js の normalizeStoredHistory と同じ上限。
const HISTORY_LIMIT = 30;

/** FNV-1a 32bit。同じ荷物かどうかの見分けにだけ使う（秘密性は要らない）。 */
export function digestOf(text) {
  let h = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h.toString(16).padStart(8, "0");
}

/**
 * `#...` から引き継ぎの荷物を取り出す。無ければ null。
 * rest は荷物を抜いたあとのハッシュ（元のページ内リンクの `#xxx` があれば残す）。
 */
export function extractCarryFromHash(hash) {
  if (typeof hash !== "string" || !hash) return null;
  const body = hash.startsWith("#") ? hash.slice(1) : hash;
  const parts = body.split("&");
  const index = parts.findIndex((part) => part.startsWith(`${CARRY_PARAM}=`));
  if (index < 0) return null;
  const encoded = parts[index].slice(CARRY_PARAM.length + 1);
  const restParts = parts.filter((_, i) => i !== index);
  const rest = restParts.join("&");
  return { encoded, rest: rest ? `#${rest}` : "" };
}

function base64UrlToUtf8(encoded) {
  if (!/^[A-Za-z0-9_-]*$/.test(encoded)) throw new Error("base64url 以外の文字が混ざっています");
  const b64 = encoded.replace(/-/g, "+").replace(/_/g, "/") + "=".repeat((4 - (encoded.length % 4)) % 4);
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return new TextDecoder("utf-8", { fatal: true }).decode(bytes);
}

/** 旧 URL 側と同じ詰め方（検査と、万一ここから送り返すときのため）。 */
export function encodeCarryPayload(payload) {
  const bytes = new TextEncoder().encode(JSON.stringify(payload));
  let binary = "";
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

/**
 * 荷物をほどく。{ ok: true, items } か { ok: false, reason }。
 * items は { favorites?: 生の文字列, history?: 生の文字列, plan?: 生の文字列 }（旧 localStorage の値そのまま）。
 */
export function decodeCarryPayload(encoded) {
  if (typeof encoded !== "string" || !encoded) return { ok: false, reason: "empty" };
  if (encoded.length > CARRY_MAX_CHARS) return { ok: false, reason: "too-large" };
  let text;
  try {
    text = base64UrlToUtf8(encoded);
  } catch (error) {
    return { ok: false, reason: "bad-base64", error };
  }
  let payload;
  try {
    payload = JSON.parse(text);
  } catch (error) {
    return { ok: false, reason: "bad-json", error };
  }
  if (!payload || typeof payload !== "object" || Array.isArray(payload)) return { ok: false, reason: "bad-shape" };
  if (payload.v !== CARRY_VERSION) return { ok: false, reason: "bad-version" };
  const source = payload.items;
  if (!source || typeof source !== "object" || Array.isArray(source)) return { ok: false, reason: "bad-shape" };
  const items = {};
  for (const name of Object.keys(CARRY_KEYS)) {
    if (typeof source[name] === "string" && source[name]) items[name] = source[name];
  }
  return { ok: true, items };
}

function parseJson(raw) {
  if (typeof raw !== "string" || !raw) return { ok: true, value: undefined };
  try {
    return { ok: true, value: JSON.parse(raw) };
  } catch (error) {
    return { ok: false, error };
  }
}

function spotIdList(value, knownIds) {
  if (!Array.isArray(value)) return null;
  return value.filter((id) => typeof id === "string" && id && (!knownIds || knownIds.has(id)));
}

/**
 * 新 URL 側の値（existing）と運んできた値（carried）を合わせる。どちらも localStorage の生の文字列。
 *   - 新側に同じキーのデータがあれば新側を優先（新側を前に置く／プランは新側を残す）
 *   - お気に入り・履歴は重複を除いて足し合わせる（履歴は 30 件まで）
 *   - 壊れた値は黙って捨てず warnings に理由を積む（呼び出し側が console.warn する）
 * 戻り値: { writes: { key: 新しい生の文字列 }, added: { favorites, history, plan }, warnings: [] }
 */
export function mergeCarried(existing, carried, { knownIds } = {}) {
  const writes = {};
  const added = { favorites: 0, history: 0, plan: false };
  const warnings = [];

  for (const name of ["favorites", "history"]) {
    if (carried[name] === undefined) continue;
    const carriedParsed = parseJson(carried[name]);
    const carriedList = carriedParsed.ok ? spotIdList(carriedParsed.value, knownIds) : null;
    if (!carriedList) {
      warnings.push(`${name}: 運んできた値が配列の JSON ではないので取り込みません`);
      continue;
    }
    const existingParsed = parseJson(existing[name]);
    let base = [];
    if (!existingParsed.ok) {
      warnings.push(`${name}: 新しい住所の値が壊れていたので、運んできた値で置き換えます`);
    } else if (existingParsed.value !== undefined && existingParsed.value !== null) {
      const list = spotIdList(existingParsed.value, null);
      if (list) base = list;
      else warnings.push(`${name}: 新しい住所の値が配列ではなかったので、運んできた値で置き換えます`);
    }
    const seen = new Set(base);
    const merged = [...base];
    for (const id of carriedList) {
      if (seen.has(id)) continue;
      seen.add(id);
      merged.push(id);
    }
    // 新側を前に置いているので、上限で切られるのは運んできた側だけ。
    const limited = name === "history" ? merged.slice(0, HISTORY_LIMIT) : merged;
    const count = Math.max(0, limited.length - base.length);
    if (count > 0) {
      writes[CARRY_KEYS[name]] = JSON.stringify(limited);
      added[name] = count;
    }
  }

  if (carried.plan !== undefined) {
    const carriedParsed = parseJson(carried.plan);
    const plan = carriedParsed.ok ? carriedParsed.value : undefined;
    const validPlan = plan && typeof plan === "object" && !Array.isArray(plan) && Array.isArray(plan.entries) && plan.entries.length > 0;
    if (!carriedParsed.ok || (plan !== null && plan !== undefined && !validPlan)) {
      warnings.push("plan: 運んできたプランの形が違うので取り込みません");
    } else if (validPlan) {
      const existingParsed = parseJson(existing.plan);
      const existingPlan = existingParsed.ok ? existingParsed.value : undefined;
      const hasExisting = existingPlan && typeof existingPlan === "object" && !Array.isArray(existingPlan);
      if (!hasExisting) {
        writes[CARRY_KEYS.plan] = carried.plan;
        added.plan = true;
      }
    }
  }

  return { writes, added, warnings };
}

/**
 * 荷物（base64url の文字列）を 1 つほどいて localStorage へ合わせる。
 * 戻り値: { status, added? }
 *   status: "imported"（1 つ以上取り込んだ） / "nothing-new"（全部すでにあった）
 *           / "already"（同じ荷物を前に取り込み済み） / "rejected"（壊れていた・大きすぎた） / "storage-error"
 */
export function importCarryPayload(encoded, { storage, logger = console, knownIds } = {}) {
  const decoded = decodeCarryPayload(encoded);
  if (!decoded.ok) {
    logger.warn(`[引き継ぎ] 前の住所から届いたデータを読めなかったので取り込みません (${decoded.reason})`, decoded.error || "");
    return { status: "rejected", reason: decoded.reason };
  }

  try {
    const digest = digestOf(encoded);
    const seenParsed = parseJson(storage.getItem(CARRY_SEEN_KEY));
    const seen = seenParsed.ok && Array.isArray(seenParsed.value) ? seenParsed.value.filter((d) => typeof d === "string") : [];
    // 旧 URL のブックマークから何度来ても、同じ荷物は一度しかほどかない
    // （新しい住所で外したお気に入りが、旧 URL を開くたびに戻ってくるのを防ぐ）。
    if (seen.includes(digest)) return { status: "already" };

    const existing = {};
    for (const [name, key] of Object.entries(CARRY_KEYS)) existing[name] = storage.getItem(key) ?? undefined;
    const { writes, added, warnings } = mergeCarried(existing, decoded.items, { knownIds });
    for (const message of warnings) logger.warn(`[引き継ぎ] ${message}`);
    for (const [key, value] of Object.entries(writes)) storage.setItem(key, value);
    storage.setItem(CARRY_SEEN_KEY, JSON.stringify([digest, ...seen].slice(0, SEEN_LIMIT)));

    const any = added.favorites > 0 || added.history > 0 || added.plan;
    return { status: any ? "imported" : "nothing-new", added };
  } catch (error) {
    logger.warn("[引き継ぎ] この端末に保存できなかったので取り込みません", error);
    return { status: "storage-error" };
  }
}

/**
 * 起動時に 1 回だけ呼ぶ（アドレス欄の `#dokoiku-carry=` で直接届いた荷物）。
 * location / history / storage / logger はブラウザのものを渡す（検査では偽物を渡す）。
 * 荷物が無ければ { status: "none" }、あれば importCarryPayload の結果。
 */
export function applyCarryOver({ location, history, storage, logger = console, knownIds } = {}) {
  const found = extractCarryFromHash(location && location.hash);
  if (!found) return { status: "none" };

  // 取り込みの成否にかかわらず、荷物はアドレス欄から先に消す（共有・ブックマークに乗せない）。
  try {
    const url = `${location.pathname || "/"}${location.search || ""}${found.rest}`;
    if (history && typeof history.replaceState === "function") history.replaceState(history.state ?? null, "", url);
  } catch (error) {
    logger.warn("[引き継ぎ] アドレス欄の荷物を消せませんでした", error);
  }

  return importCarryPayload(found.encoded, { storage, logger, knownIds });
}

/**
 * 起動時に 1 回だけ呼ぶ（carry.html が置いていった荷物）。荷物は成否にかかわらず片づける
 * （壊れた荷物を毎回読み直さない）。荷物が無ければ { status: "none" }。
 */
export function applyPendingCarryOver({ storage, logger = console, knownIds } = {}) {
  let encoded;
  try {
    encoded = storage.getItem(CARRY_PENDING_KEY);
    if (!encoded) return { status: "none" };
    storage.removeItem(CARRY_PENDING_KEY);
  } catch (error) {
    logger.warn("[引き継ぎ] 預かっていた荷物を読めませんでした", error);
    return { status: "storage-error" };
  }
  return importCarryPayload(encoded, { storage, logger, knownIds });
}

/** 2 つの取り込み結果の件数を足す（ハッシュと預かり分の両方が同時に来たとき用）。 */
export function sumAdded(...results) {
  const total = { favorites: 0, history: 0, plan: false };
  for (const r of results) {
    if (!r || r.status !== "imported" || !r.added) continue;
    total.favorites += r.added.favorites || 0;
    total.history += r.added.history || 0;
    total.plan = total.plan || Boolean(r.added.plan);
  }
  return total;
}

/** 取り込み結果の案内文。取り込んだものが無ければ null。 */
export function carryOverMessage(added) {
  if (!added) return null;
  const parts = [];
  if (added.favorites > 0) parts.push(`お気に入り${added.favorites}件`);
  if (added.history > 0) parts.push(`履歴${added.history}件`);
  if (added.plan) parts.push("保存したプラン");
  if (!parts.length) return null;
  return {
    title: "お引っ越し完了！",
    body: `${parts.join("・")}を、段ボールごと新しい住所へ運び込みました。`,
    hint: "ブックマークはこの新しい住所で付け直しておくと安心です。",
  };
}
